import {
  DigitalProduct,
  PrintProduct,
} from "../../../../../common/api/decimal/methods/product.ts";
import {
  isDigitalProduct,
  isOutOfStock,
  isPrintProduct,
} from "../../../product/common/productUtils.ts";
import { CourseMaterial } from "../coursesLoader.ts";
import { CartItem } from "../../../../../common/api/decimal/methods/cart.ts";
import { SystemKind } from "../../../../../common/api/decimal/common-types/kind.ts";
import { MaterialFormat } from "../../../../../common/api/decimal/methods/course.ts";

const getMaterialKind = (material: CourseMaterial): SystemKind | null => {
  // not a general lookup returns null to match first product in getStoreProducts
  type MaterialFormats = Extract<
    keyof CourseMaterial["materialFormat"],
    "etextbook" | "courseware" | "bundle"
  >;
  const lookup: Record<MaterialFormats, SystemKind> = {
    etextbook: "book",
    courseware: "Online Resource",
    bundle: "Online Resource Bundle",
  };
  return lookup[material.materialFormat as MaterialFormats] ?? null;
};

export function getStoreProducts(material: CourseMaterial) {
  const product = material.product;

  const adoptedProduct = getAdoptedProduct(material);

  const digitalProducts = product?.filter((p) => isDigitalProduct(p)) as
    | DigitalProduct[]
    | undefined;
  const materialKind = getMaterialKind(material);
  // adopted isbn is a priority, but we'll take a kind match if not
  const digitalProduct =
    adoptedProduct && isDigitalProduct(adoptedProduct)
      ? adoptedProduct
      : digitalProducts?.find((p) => p.kind === materialKind) ??
        digitalProducts?.[0];

  const printProducts = product?.filter((p) =>
    isPrintProduct(p)
  ) as PrintProduct[];
  // if print only we will only accept a direct isbn match as the user might buy
  // a version that has not been adopted, if we have a digital product then
  // the print product is more an add-on and we are less bothered
  const printProduct =
    adoptedProduct && isPrintProduct(adoptedProduct)
      ? adoptedProduct
      : digitalProduct
      ? printProducts[0]
      : undefined;

  const isPrintOutOfStock = printProduct?.variants?.every((variant) =>
    isOutOfStock(variant)
  );

  return {
    digitalProduct,
    printProduct,
    defaultVariant: (digitalProduct ?? printProduct)?.variants?.[0],
    isPrintOutOfStock,
    isOutOfStock: !!(!digitalProduct && isPrintOutOfStock),
    adoptedProduct,
  };
}

export function findMaterialInCart(
  material: CourseMaterial,
  lineItems: CartItem[] = []
) {
  const { digitalProduct, printProduct } = getStoreProducts(material);
  const productIsbns = [material.vbid];
  if (digitalProduct?.canonicalIsbn) {
    productIsbns.push(digitalProduct?.canonicalIsbn);
  }
  if (printProduct?.canonicalIsbn) {
    productIsbns.push(printProduct?.canonicalIsbn);
  }
  return lineItems.find((item) => productIsbns.includes(item.canonicalIsbn));
}

export function isMaterialInStoreCart(
  material: CourseMaterial,
  lineItems: CartItem[] = []
) {
  return findMaterialInCart(material, lineItems) !== undefined;
}

export function isMaterialOutOfStock(material: CourseMaterial) {
  const { isOutOfStock } = getStoreProducts(material);
  return isOutOfStock;
}

export function getAdoptedProduct(material: CourseMaterial) {
  return material.product?.find((p) => {
    return material.vbid === p.canonicalIsbn && p.canonicalIsbn;
  });
}

export function kindToMaterialFormat(kind: SystemKind): MaterialFormat {
  const lookup: Partial<Record<SystemKind, MaterialFormat>> = {
    "Online Resource Bundle": "bundle",
    "Online Resource": "courseware",
    "Print Book": "print",
    OER: "oer",
    // for now makes supplies same as print to get the right cover
    Supplies: "print",
  };
  return lookup[kind] ?? "etextbook";
}
